import type { RankingPlayer, RankingLeaderboardMetric, RankingPartialStandingsData } from '../types';

/** Numeric value of a leaderboard metric — what the list sorts by. */
export function metricNumber(player: RankingPlayer, metric: RankingLeaderboardMetric): number {
  switch (metric) {
    case 'win_rate':
      return player.matchesPlayed > 0 ? player.matchesWon / player.matchesPlayed : 0;
    case 'game_balance':
      return player.gamesWon - player.gamesLost;
    case 'total_points':
      return player.totalPoints;
    case 'matches_won':
      return player.matchesWon;
    case 'matches_lost':
      return player.matchesLost;
    case 'matches_played':
      return player.matchesPlayed;
    case 'games_won':
      return player.gamesWon;
    case 'rounds_played':
      return player.roundsPlayed;
  }
}

/** Server standings order; a member without a position yet sorts last. */
export function compareStanding(a: RankingPlayer, b: RankingPlayer): number {
  const pa = a.standingPosition ?? Number.MAX_SAFE_INTEGER;
  const pb = b.standingPosition ?? Number.MAX_SAFE_INTEGER;
  if (pa !== pb) return pa - pb;
  return (a.name || '').localeCompare(b.name || '', 'pt-BR') || a.id.localeCompare(b.id);
}

/**
 * The number shown next to a row. On Pontos it is the server's standings
 * position, so two fully tied players both read "3"; other metrics simply
 * count down the list.
 */
export function leaderboardPosition(
  player: RankingPlayer,
  index: number,
  metric: RankingLeaderboardMetric
): number {
  return metric === 'total_points' ? (player.standingPosition ?? index + 1) : index + 1;
}

/**
 * Whether the partial view ("as if the season ended today") would change
 * anything. Each player keeps their best `held − discards` rounds, where a
 * round in progress only counts as held for a player once they have a result
 * in it (the backend's `playerRoundsHeld`) — so a player's `held` lies between
 * the completed rounds and every round started. The toggle shows while some
 * player in that range would land on a limit above zero and below the
 * official one.
 */
export function partialDiscardsApply(
  totalRounds: number | null,
  discardRounds: number,
  roundsCompleted: number,
  roundsStarted: number
): boolean {
  if (!totalRounds || discardRounds <= 0) return false;
  return roundsStarted - discardRounds > 0 && roundsCompleted - discardRounds < totalRounds - discardRounds;
}

/** Players with their counters and position swapped for the partial view's. */
export function withPartialStandings(
  players: RankingPlayer[],
  partial: RankingPartialStandingsData
): RankingPlayer[] {
  const byId = new Map(partial.players.map((p) => [p.playerId, p]));
  return players.map((player) => {
    const standing = byId.get(player.id);
    if (!standing) return { ...player, standingPosition: null };
    const { playerId: _playerId, ...stats } = standing;
    return { ...player, ...stats };
  });
}
